import * as Sentry from "@sentry/react";
import { Send } from "lucide-react";
import React from "react";

import { AttachmentsButton } from "./attachments/attachments-button";
import { AttachmentsManager } from "./attachments/attachments-manager";
import { Attachment } from "./attachments/types";
import { useAttachments } from "./attachments/use-attachments";
import {
  AudioButtonRecording,
  AudioDiscardButton,
} from "./audio/audio-buttons";
import { FullAudioVisualizer } from "./audio/audio-control";
import {
  AudioRecordContextStatus,
  useAudioRecorder,
} from "./audio/use-audio-recorder";
import { CenteredBarsVisualizer } from "./audio/visualizers";
import { SelfAdjustingTextarea } from "./self-adjusting-textarea";
import { useCurrentNode } from "../app-shell/providers/current-note-provider";
import { useSendGtmEvents } from "../app-shell/providers/gtm-events-provider/gtm-events-provider";
import { cn } from "~/util/cn";
import { useWindowDimensions } from "~/util/hooks";
import "./self-adjusting-textarea-with-attachments.css";

export const SelfAdjustingTextareaWithAttachments: React.FC<{
  text: string;
  setText: React.Dispatch<React.SetStateAction<string>>;
  onSubmit: (ctx: {
    payload: {
      text: string;
      base64Audio: string;
      attachments: Attachment[];
    };
    onDone: () => void;
  }) => void;
  autoFocus?: boolean;
  disableSubmit?: boolean;
}> = ({ text, setText, onSubmit, autoFocus, disableSubmit }) => {
  const { width } = useWindowDimensions();
  const recorder = useAudioRecorder();
  const attachments = useAttachments();
  const { node } = useCurrentNode();
  const { sendAudioMessageSent, sendTextMessageSent } = useSendGtmEvents();

  const handleSubmit = () => {
    const onDone = () => {
      setText("");
      recorder.cleanupResources();
      attachments.reset();
    };

    if (!node) {
      Sentry.captureException(
        new Error(
          `node not existing when dispatching gtm for ${
            recorder.status === AudioRecordContextStatus.Playback
              ? "audio_record_start"
              : "text_message_sent"
          }`,
        ),
        {
          level: "error",
        },
      );
      return;
    }

    if (recorder.status === AudioRecordContextStatus.Playback) {
      sendAudioMessageSent({ duration: recorder.duration });

      onSubmit({
        payload: {
          text: "",
          base64Audio: recorder.audioBase64,
          attachments: [],
        },
        onDone,
      });
    } else {
      sendTextMessageSent({ hasFileAttached: attachments.list.length > 0 });

      onSubmit({
        payload: {
          text: text.trim(),
          base64Audio: "",
          attachments: attachments.list,
        },
        onDone,
      });
    }
  };

  return (
    <div className={cn("layout", recorder.status)}>
      {recorder.status == AudioRecordContextStatus.Init ? (
        <>
          {attachments.hasAttachments ? (
            <div className="attachments">
              <AttachmentsManager attachments={attachments} />
            </div>
          ) : null}

          <div className="text">
            <SelfAdjustingTextarea
              text={text}
              setText={setText}
              className="max-h-48 w-full resize-none appearance-none break-words bg-transparent px-1 py-2 leading-6 text-neutral-900 caret-neutral-900 outline-none sm:max-h-96"
              onEnterPressed={handleSubmit}
              autoFocus={autoFocus}
            />
          </div>
          <div className="attachment">
            <AttachmentsButton attachments={attachments} />
          </div>
        </>
      ) : null}

      {recorder.isSupported &&
      recorder.status !== AudioRecordContextStatus.Playback ? (
        <div className="mic">
          <AudioButtonRecording
            recorder={recorder}
            onStartRecording={attachments.reset}
          />
        </div>
      ) : null}

      {recorder.status === AudioRecordContextStatus.Init ? (
        <div className="send">
          <button
            title="send"
            className="btn-neutral btn-sm btn ml-auto grid h-10 w-12 place-content-center rounded-btn transition-opacity duration-500 ease-in-out disabled:bg-neutral-700 disabled:text-neutral-100 disabled:opacity-40"
            onClick={handleSubmit}
            disabled={disableSubmit}
          >
            <Send width={20} />
          </button>
        </div>
      ) : null}

      {recorder.status === AudioRecordContextStatus.Recording ? (
        <>
          <div className="track flex items-center justify-end">
            <CenteredBarsVisualizer
              visualData={recorder.visualData}
              sliceLength={width < 450 ? 40 : 70}
            />
          </div>

          <div className="duration grid place-content-center">
            <span className="text-xs font-medium text-primary">
              {recorder.elapsedTimeFormatted}
            </span>
          </div>
        </>
      ) : null}

      {recorder.status === AudioRecordContextStatus.Playback ? (
        <>
          <div className="playback-discard">
            <AudioDiscardButton recorder={recorder} />
          </div>
          <div className="playback">
            <FullAudioVisualizer
              audioData={recorder.visualData}
              audioSrc={recorder.audioUrl}
              duration={recorder.duration}
            />
          </div>
          <div className="playback-send">
            <button
              title="send"
              type="button"
              className="shadow-btn btn-neutral btn-sm btn h-10 w-12 transition-opacity duration-500 ease-in-out disabled:bg-neutral-700 disabled:text-neutral-100 disabled:opacity-40"
              onClick={handleSubmit}
              disabled={disableSubmit}
            >
              <Send width={20} />
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
};
